import React from "react";
import { t } from "ttag";

import { getWindow } from "@thelabnyc/thelabui/src/utils/dom";

import { eventuallySendMetric } from "../../../api/metrics";
import { cancelOrder, loadOrderCancellationInfo } from "../../../api/orders";
import { IOrderID } from "../../../models/nominals";
import { OrderCancellationInfo } from "../../../models/orders";
import { CancelModal } from "../CancelModal";
import { CancelModalViewState, OTHER_OPTION } from "../models";

const getReason = (
    selectedReason: string | null,
    enteredReason: string,
): string => {
    const reason =
        selectedReason === OTHER_OPTION ? enteredReason : selectedReason;
    return reason || "";
};

export const OrderCancelButton = (props: {
    orderID: IOrderID;
}): React.ReactNode => {
    const [viewState, setViewState] = React.useState<CancelModalViewState>(
        CancelModalViewState.CLOSED,
    );
    const [cancellationInfo, setCancellationInfo] =
        React.useState<OrderCancellationInfo | null>(null);
    const [selectedReason, setSelectedReason] = React.useState<string | null>(
        null,
    );
    const [enteredReason, setEnteredReason] = React.useState<string>("");

    const onCancelButtonClick = async () => {
        // Set state to loading to disable the open button
        setViewState(CancelModalViewState.LOADING);
        // Record an event that the user has opened the cancel modal.
        eventuallySendMetric({
            name: "OrderCancelButton.open-modal",
            dimensions: [],
            type: "BOOLEAN",
            value: "TRUE",
        });
        // Load the cancellation info and use it to open the reason form.
        try {
            const data = await loadOrderCancellationInfo(props.orderID);
            setViewState(
                data.can_be_cancelled
                    ? CancelModalViewState.SELECT_REASON
                    : CancelModalViewState.ERROR,
            );
            setCancellationInfo(data);
        } catch {
            setViewState(CancelModalViewState.ERROR);
        }
    };

    const onRequestClose = () => {
        setViewState(CancelModalViewState.CLOSED);
    };

    const onSelectReason = (reason: string) => {
        setSelectedReason(reason);
        setEnteredReason("");
    };

    const onEnterReason = (reason: string) => {
        setSelectedReason(OTHER_OPTION);
        setEnteredReason(reason);
    };

    const onSubmit = async () => {
        setViewState(CancelModalViewState.SAVING);
        // Record an event that the user has opened the cancel modal.
        eventuallySendMetric({
            name: "OrderCancelButton.cancel-order",
            dimensions: [],
            type: "BOOLEAN",
            value: "TRUE",
        });
        // Cancel the order
        const reason = getReason(selectedReason, enteredReason);
        try {
            await cancelOrder(props.orderID, reason);
            setViewState(CancelModalViewState.CLOSED);
            // Reload the page so that the new order status is shown.
            const window = getWindow();
            if (window) {
                window.location.reload();
            }
        } catch {
            setViewState(CancelModalViewState.ERROR);
        }
    };

    return (
        <>
            <button
                onClick={onCancelButtonClick}
                disabled={viewState !== CancelModalViewState.CLOSED}
                type="button"
                className="button"
            >
                {t`Cancel Order`}
            </button>
            <CancelModal
                viewState={viewState}
                cancellationInfo={cancellationInfo}
                selectedReason={selectedReason}
                enteredReason={enteredReason}
                onSelectReason={onSelectReason}
                onEnterReason={onEnterReason}
                onRequestClose={onRequestClose}
                onSubmit={onSubmit}
            />
        </>
    );
};
